/**
 * Intelligent Conversation Automation Engine
 * 
 * Source-based response logic, automated sequences,
 * AI response generation, and booking optimization.
 */

import type { Opportunity } from "./opportunityTypes";
import type { Conversation, Message } from "./communicationTypes";

/* ═══════ SOURCE RESPONSE PROFILES ═══════ */

export type ResponseTone = "aggressive" | "consultative" | "soft" | "neutral";
export type AutomationMode = "auto_send" | "suggest" | "disabled";

export interface SourceResponseProfile {
  source: string;
  tone: ResponseTone;
  urgency: "immediate" | "standard" | "relaxed";
  /** Timing rules for each step (minutes from lead creation) */
  sequence: SequenceStep[];
  bookingPush: "aggressive" | "moderate" | "gentle";
  automationMode: AutomationMode;
}

export interface SequenceStep {
  id: string;
  name: string;
  delayMinutes: number;
  channel: "sms" | "call" | "email";
  templateKey: string;
  /** If true, skip if customer has replied */
  skipOnReply: boolean;
  bookingCTA: boolean;
}

export const SOURCE_RESPONSE_PROFILES: SourceResponseProfile[] = [
  {
    source: "Angi",
    tone: "aggressive",
    urgency: "immediate",
    bookingPush: "aggressive",
    automationMode: "suggest",
    sequence: [
      { id: "angi-1", name: "Instant Response", delayMinutes: 0, channel: "sms", templateKey: "instant_intro", skipOnReply: false, bookingCTA: true },
      { id: "angi-2", name: "10-min Follow-up", delayMinutes: 10, channel: "sms", templateKey: "value_prop", skipOnReply: true, bookingCTA: true },
      { id: "angi-3", name: "1-hour Call", delayMinutes: 60, channel: "call", templateKey: "call_intro", skipOnReply: true, bookingCTA: false },
      { id: "angi-4", name: "Day 1 AM", delayMinutes: 1440, channel: "sms", templateKey: "day1_followup", skipOnReply: true, bookingCTA: true },
      { id: "angi-5", name: "Day 2 PM", delayMinutes: 2880, channel: "sms", templateKey: "urgency_offer", skipOnReply: true, bookingCTA: true },
      { id: "angi-6", name: "Day 3 Final", delayMinutes: 4320, channel: "sms", templateKey: "final_attempt", skipOnReply: true, bookingCTA: true },
    ],
  },
  {
    source: "HomeAdvisor",
    tone: "aggressive",
    urgency: "immediate",
    bookingPush: "aggressive",
    automationMode: "suggest",
    sequence: [
      { id: "ha-1", name: "Instant Response", delayMinutes: 0, channel: "sms", templateKey: "instant_intro", skipOnReply: false, bookingCTA: true },
      { id: "ha-2", name: "15-min Follow-up", delayMinutes: 15, channel: "sms", templateKey: "value_prop", skipOnReply: true, bookingCTA: true },
      { id: "ha-3", name: "1-hour Call", delayMinutes: 60, channel: "call", templateKey: "call_intro", skipOnReply: true, bookingCTA: false },
      { id: "ha-4", name: "Day 1", delayMinutes: 1440, channel: "sms", templateKey: "day1_followup", skipOnReply: true, bookingCTA: true },
      { id: "ha-5", name: "Day 2", delayMinutes: 2880, channel: "sms", templateKey: "urgency_offer", skipOnReply: true, bookingCTA: true },
    ],
  },
  {
    source: "Google Ads",
    tone: "consultative",
    urgency: "immediate",
    bookingPush: "moderate",
    automationMode: "suggest",
    sequence: [
      { id: "ga-1", name: "Instant Response", delayMinutes: 0, channel: "sms", templateKey: "instant_intro", skipOnReply: false, bookingCTA: true },
      { id: "ga-2", name: "10-min Value", delayMinutes: 10, channel: "sms", templateKey: "value_prop", skipOnReply: true, bookingCTA: true },
      { id: "ga-3", name: "30-min Call", delayMinutes: 30, channel: "call", templateKey: "call_intro", skipOnReply: true, bookingCTA: false },
      { id: "ga-4", name: "Day 1", delayMinutes: 1440, channel: "sms", templateKey: "day1_followup", skipOnReply: true, bookingCTA: true },
      { id: "ga-5", name: "Day 2", delayMinutes: 2880, channel: "sms", templateKey: "social_proof", skipOnReply: true, bookingCTA: true },
      { id: "ga-6", name: "Day 3", delayMinutes: 4320, channel: "email", templateKey: "final_attempt", skipOnReply: true, bookingCTA: true },
    ],
  },
  {
    source: "Facebook Ads",
    tone: "consultative",
    urgency: "immediate",
    bookingPush: "moderate",
    automationMode: "suggest",
    sequence: [
      { id: "fb-1", name: "Instant Response", delayMinutes: 0, channel: "sms", templateKey: "instant_intro", skipOnReply: false, bookingCTA: true },
      { id: "fb-2", name: "15-min Follow-up", delayMinutes: 15, channel: "sms", templateKey: "value_prop", skipOnReply: true, bookingCTA: true },
      { id: "fb-3", name: "1-hour Call", delayMinutes: 60, channel: "call", templateKey: "call_intro", skipOnReply: true, bookingCTA: false },
      { id: "fb-4", name: "Day 1", delayMinutes: 1440, channel: "sms", templateKey: "day1_followup", skipOnReply: true, bookingCTA: true },
      { id: "fb-5", name: "Day 3", delayMinutes: 4320, channel: "sms", templateKey: "final_attempt", skipOnReply: true, bookingCTA: true },
    ],
  },
  {
    source: "Website",
    tone: "consultative",
    urgency: "standard",
    bookingPush: "moderate",
    automationMode: "suggest",
    sequence: [
      { id: "web-1", name: "Welcome", delayMinutes: 2, channel: "sms", templateKey: "instant_intro", skipOnReply: false, bookingCTA: true },
      { id: "web-2", name: "30-min Value", delayMinutes: 30, channel: "sms", templateKey: "value_prop", skipOnReply: true, bookingCTA: true },
      { id: "web-3", name: "Day 1 Call", delayMinutes: 1440, channel: "call", templateKey: "call_intro", skipOnReply: true, bookingCTA: false },
      { id: "web-4", name: "Day 2 Email", delayMinutes: 2880, channel: "email", templateKey: "social_proof", skipOnReply: true, bookingCTA: true },
    ],
  },
  {
    source: "Referral",
    tone: "soft",
    urgency: "relaxed",
    bookingPush: "gentle",
    automationMode: "suggest",
    sequence: [
      { id: "ref-1", name: "Thank You", delayMinutes: 5, channel: "sms", templateKey: "referral_thanks", skipOnReply: false, bookingCTA: false },
      { id: "ref-2", name: "Introduction", delayMinutes: 60, channel: "call", templateKey: "call_intro", skipOnReply: true, bookingCTA: false },
      { id: "ref-3", name: "Day 1", delayMinutes: 1440, channel: "sms", templateKey: "gentle_followup", skipOnReply: true, bookingCTA: true },
      { id: "ref-4", name: "Day 3", delayMinutes: 4320, channel: "sms", templateKey: "gentle_followup", skipOnReply: true, bookingCTA: true },
    ],
  },
  {
    source: "Home Show",
    tone: "soft",
    urgency: "relaxed",
    bookingPush: "gentle",
    automationMode: "suggest",
    sequence: [
      { id: "hs-1", name: "Thank You", delayMinutes: 30, channel: "sms", templateKey: "homeshow_thanks", skipOnReply: false, bookingCTA: true },
      { id: "hs-2", name: "Day 1 Call", delayMinutes: 1440, channel: "call", templateKey: "call_intro", skipOnReply: true, bookingCTA: false },
      { id: "hs-3", name: "Day 2", delayMinutes: 2880, channel: "sms", templateKey: "gentle_followup", skipOnReply: true, bookingCTA: true },
    ],
  },
];

/* ═══════ MESSAGE TEMPLATES ═══════ */

export interface MessageTemplate {
  key: string;
  label: string;
  toneVariants: Record<ResponseTone, string>;
  bookingVariant?: string; // Appended when bookingCTA is true
}

export const MESSAGE_TEMPLATES: MessageTemplate[] = [
  {
    key: "instant_intro",
    label: "Instant Introduction",
    toneVariants: {
      aggressive: "Hi {name}! This is {rep} with Koredor. We just received your request and I'm ready to help you RIGHT NOW. Can I call you in the next 2 minutes? We have crews available this week!",
      consultative: "Hi {name}, this is {rep} from Koredor. Thank you for reaching out about your project. I'd love to learn more about what you have in mind. When is a good time to chat?",
      soft: "Hi {name}, this is {rep} from Koredor. Thank you so much for your interest. I'd love to hear about your project whenever you have a moment.",
      neutral: "Hi {name}, this is {rep} from Koredor. We received your inquiry and would love to help. Please let us know a good time to connect.",
    },
    bookingVariant: "\n\nWould you prefer morning or afternoon for a free consultation?",
  },
  {
    key: "value_prop",
    label: "Value Proposition",
    toneVariants: {
      aggressive: "Hey {name}! Just wanted to make sure you saw my message. Koredor has completed 500+ projects in your area with a 4.9★ rating. Let's get your free estimate scheduled TODAY!",
      consultative: "{name}, I wanted to share that we specialize in projects like yours. Our team has completed 500+ similar installations with top-rated results. Happy to walk you through our process.",
      soft: "{name}, in case it's helpful — we've been serving your area for years and have great reviews from neighbors. No pressure at all, just here when you're ready.",
      neutral: "{name}, Koredor has an excellent track record in your area. We'd love to provide a free, no-obligation estimate for your project.",
    },
    bookingVariant: "\n\nI have openings this week — want me to pencil you in?",
  },
  {
    key: "call_intro",
    label: "Call Script",
    toneVariants: {
      aggressive: "CALL: Intro, confirm project type, pitch same-week availability, close for appointment.",
      consultative: "CALL: Greet warmly, ask about project goals, discuss options, suggest consultation time.",
      soft: "CALL: Thank for referral/interest, ask questions about their vision, offer to meet at their convenience.",
      neutral: "CALL: Introduce yourself, confirm interest, offer consultation.",
    },
  },
  {
    key: "day1_followup",
    label: "Day 1 Follow-up",
    toneVariants: {
      aggressive: "Good morning {name}! Still thinking about your project? I've got a slot open today that I'm holding for you. Spots fill fast this time of year!",
      consultative: "Hi {name}, just checking in from yesterday. If you have any questions about your project or our process, I'm happy to help.",
      soft: "Hi {name}, just wanted to follow up gently. No rush — I'm here whenever you'd like to discuss your project.",
      neutral: "Hi {name}, following up on our previous conversation. Let us know if you have any questions.",
    },
    bookingVariant: "\n\nShall I schedule a time that works for you?",
  },
  {
    key: "urgency_offer",
    label: "Urgency + Offer",
    toneVariants: {
      aggressive: "{name}, we're running a limited promotion this month. If you book your consultation this week, you'll lock in our current pricing before the seasonal increase. Don't miss out!",
      consultative: "{name}, I wanted to mention we have some great availability this week if you're ready to explore options. Happy to work around your schedule.",
      soft: "{name}, just a friendly note that our calendar is looking good this week if you'd like to chat. Absolutely no pressure.",
      neutral: "{name}, we have good availability this week for consultations. Let us know if you'd like to schedule.",
    },
    bookingVariant: "\n\nReply with your preferred day and I'll confirm it immediately.",
  },
  {
    key: "social_proof",
    label: "Social Proof",
    toneVariants: {
      aggressive: "{name}, your neighbor on {street} just had their project completed last month and they're THRILLED. Let me show you what we can do for your home!",
      consultative: "{name}, we recently completed a similar project nearby and the homeowner was very happy with the results. Would you like to see some photos?",
      soft: "{name}, I thought you might enjoy seeing a recent project we completed in your area. Happy to share photos if you're interested.",
      neutral: "{name}, we recently completed a project in your area. Let us know if you'd like references or to see photos.",
    },
    bookingVariant: "\n\nWant to see it in person? I can schedule a time for you.",
  },
  {
    key: "final_attempt",
    label: "Final Attempt",
    toneVariants: {
      aggressive: "{name}, I don't want to bother you, but I wanted to reach out one last time. We're here when you're ready. Reply STOP to opt out, or YES and I'll call you right away!",
      consultative: "{name}, I wanted to check in one more time. If the timing isn't right, I completely understand. We'll be here whenever you're ready to move forward.",
      soft: "{name}, just one last note — I'm here whenever you're ready, no rush at all. Wishing you all the best with your project.",
      neutral: "{name}, this is our final follow-up. We'd love to help with your project whenever you're ready. Don't hesitate to reach out.",
    },
  },
  {
    key: "referral_thanks",
    label: "Referral Thank You",
    toneVariants: {
      aggressive: "Hi {name}! Thank you for the referral — we love working with friends of great customers! Let's get your project started right away!",
      consultative: "Hi {name}, we were referred to you and I wanted to personally say thank you. I'd love to hear about your project when you have a moment.",
      soft: "Hi {name}, someone you trust recommended us, and that means a lot. I'd love to chat about your project whenever is convenient for you.",
      neutral: "Hi {name}, thank you for the referral. We look forward to learning about your project.",
    },
  },
  {
    key: "gentle_followup",
    label: "Gentle Follow-up",
    toneVariants: {
      aggressive: "Hey {name}! Quick check — are you still thinking about your project? I've got some exciting options to share!",
      consultative: "Hi {name}, just a friendly check-in. Any questions I can help with regarding your project?",
      soft: "Hi {name}, just wanted to make sure you know I'm here if you need anything. No rush at all.",
      neutral: "Hi {name}, checking in on your project interest. Let us know if we can help.",
    },
    bookingVariant: "\n\nWould you like to schedule a no-obligation consultation?",
  },
  {
    key: "homeshow_thanks",
    label: "Home Show Thank You",
    toneVariants: {
      aggressive: "Hey {name}! Great meeting you at the home show! Let's lock in that special show pricing before it expires. Can I call you today?",
      consultative: "Hi {name}, it was great meeting you at the home show. I'd love to continue our conversation about your project.",
      soft: "Hi {name}, so nice meeting you at the home show! I'm here whenever you'd like to chat more about your project.",
      neutral: "Hi {name}, thanks for stopping by our booth at the home show. We'd love to help with your project.",
    },
    bookingVariant: "\n\nReady to schedule your free in-home consultation?",
  },
];

/* ═══════ SEQUENCE STATUS TRACKING ═══════ */

export type StepStatus = "completed" | "pending" | "skipped" | "scheduled";

export interface SequenceStatus {
  leadId: string;
  source: string;
  profile: SourceResponseProfile;
  steps: {
    step: SequenceStep;
    status: StepStatus;
    scheduledAt: string;
    completedAt?: string;
    skippedReason?: string;
  }[];
  currentStepIndex: number;
  isComplete: boolean;
  hasCustomerReplied: boolean;
}

export function getSequenceStatus(
  lead: Opportunity,
  conversations: Conversation[],
  messages: Message[],
): SequenceStatus | null {
  const source = lead.leadSource || "Unknown";
  const profile = SOURCE_RESPONSE_PROFILES.find((p) => p.source === source)
    || { ...SOURCE_RESPONSE_PROFILES.find((p) => p.source === "Website")!, source };

  const convo = conversations.find((c) => c.linkedLeadId === lead.id || c.linkedQuoteId === lead.id);
  const convMsgs = convo ? messages.filter((m) => m.conversationId === convo.id) : [];
  const hasCustomerReplied = convMsgs.some((m) => m.direction === "inbound");
  const outboundCount = convMsgs.filter((m) => m.direction === "outbound").length;

  const leadCreatedMs = new Date(lead.createdAt).getTime();
  const nowMs = Date.now();
  const elapsedMinutes = (nowMs - leadCreatedMs) / (1000 * 60);

  const steps = profile.sequence.map((step, i) => {
    const scheduledAt = new Date(leadCreatedMs + step.delayMinutes * 60 * 1000).toISOString();

    if (step.skipOnReply && hasCustomerReplied) {
      return { step, status: "skipped" as StepStatus, scheduledAt, skippedReason: "Customer replied" };
    }

    if (i < outboundCount) {
      return { step, status: "completed" as StepStatus, scheduledAt, completedAt: scheduledAt };
    }

    if (elapsedMinutes >= step.delayMinutes) {
      return { step, status: "pending" as StepStatus, scheduledAt };
    }

    return { step, status: "scheduled" as StepStatus, scheduledAt };
  });

  const currentStepIndex = steps.findIndex((s) => s.status === "pending");
  const isComplete = steps.every((s) => s.status === "completed" || s.status === "skipped");

  return { leadId: lead.id, source, profile, steps, currentStepIndex, isComplete, hasCustomerReplied };
}

/* ═══════ AI RESPONSE GENERATION ═══════ */

export interface AIResponseSuggestion {
  id: string;
  message: string;
  confidence: number;
  reasoning: string;
  tone: ResponseTone;
  bookingCTA: boolean;
  isEdited: boolean;
}

export function generateAIResponse(
  lead: Opportunity,
  conversations: Conversation[],
  messages: Message[],
  templateKey?: string,
): AIResponseSuggestion {
  const source = lead.leadSource || "Unknown";
  const profile = SOURCE_RESPONSE_PROFILES.find((p) => p.source === source);
  const tone = profile?.tone || "neutral";
  const bookingPush = profile?.bookingPush || "moderate";

  // Find the template
  const template = templateKey
    ? MESSAGE_TEMPLATES.find((t) => t.key === templateKey)
    : MESSAGE_TEMPLATES[0];

  let message = template?.toneVariants[tone] || template?.toneVariants.neutral || "";

  // Fill template variables
  message = message
    .replace(/\{name\}/g, lead.customerName || lead.primaryName || "there")
    .replace(/\{rep\}/g, lead.assignedRep || "your Koredor team")
    .replace(/\{street\}/g, lead.address?.split(",")[0] || "your street");

  // Append booking CTA if appropriate
  if (bookingPush !== "gentle" && template?.bookingVariant) {
    message += template.bookingVariant;
  }

  // Calculate confidence based on data available
  const convo = conversations.find((c) => c.linkedLeadId === lead.id);
  const convMsgs = convo ? messages.filter((m) => m.conversationId === convo.id) : [];
  const hasContext = convMsgs.length > 0;
  const confidence = hasContext ? 85 : 72;

  return {
    id: `ai-${Date.now()}`,
    message,
    confidence,
    reasoning: `${tone} tone for ${source} lead. ${hasContext ? "Context from " + convMsgs.length + " previous messages." : "Initial outreach — no prior conversation."}`,
    tone,
    bookingCTA: bookingPush !== "gentle",
    isEdited: false,
  };
}

/* ═══════ BOOKING OPTIMIZATION ═══════ */

export interface BookingOptimization {
  suggestedTimes: string[];
  noShowRisk: "low" | "medium" | "high";
  confirmationNeeded: boolean;
  reminderSchedule: string[];
  engagementTip: string;
}

export function getBookingOptimization(
  lead: Opportunity,
  conversations: Conversation[],
  messages: Message[],
): BookingOptimization {
  const convo = conversations.find((c) => c.linkedLeadId === lead.id);
  const convMsgs = convo ? messages.filter((m) => m.conversationId === convo.id) : [];
  const inboundCount = convMsgs.filter((m) => m.direction === "inbound").length;

  // Determine no-show risk
  const noShowRisk: "low" | "medium" | "high" =
    inboundCount >= 3 ? "low" :
    inboundCount >= 1 ? "medium" : "high";

  const confirmationNeeded = noShowRisk !== "low";

  // Build reminder schedule based on risk
  const reminderSchedule = noShowRisk === "high"
    ? ["24h before", "2h before", "30min before"]
    : noShowRisk === "medium"
    ? ["24h before", "2h before"]
    : ["24h before"];

  // Suggested times (next business days, morning slots preferred)
  const now = new Date();
  const suggestedTimes: string[] = [];
  for (let d = 1; d <= 5; d++) {
    const date = new Date(now.getTime() + d * 86400000);
    if (date.getDay() === 0 || date.getDay() === 6) continue;
    suggestedTimes.push(`${date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} 10:00 AM`);
    if (suggestedTimes.length >= 3) break;
  }

  const engagementTip =
    noShowRisk === "high"
      ? "High no-show risk — send confirmation text + call 2hrs before appointment"
      : noShowRisk === "medium"
      ? "Moderate engagement — confirm appointment via text 24hrs before"
      : "Strong engagement — standard reminder sufficient";

  return { suggestedTimes, noShowRisk, confirmationNeeded, reminderSchedule, engagementTip };
}

/* ═══════ TONE DISPLAY CONFIG ═══════ */

export const TONE_CONFIG: Record<ResponseTone, { label: string; color: string; bg: string }> = {
  aggressive:   { label: "Aggressive",   color: "text-warning",       bg: "bg-warning/10 border-warning/30" },
  consultative: { label: "Consultative", color: "text-primary",          bg: "bg-primary/10 border-primary/20" },
  soft:         { label: "Soft",         color: "text-success",      bg: "bg-success/10 border-success/30" },
  neutral:      { label: "Neutral",      color: "text-muted-foreground", bg: "bg-muted border-muted" },
};

export const MODE_CONFIG: Record<AutomationMode, { label: string; description: string }> = {
  auto_send: { label: "Automated", description: "Messages sent automatically" },
  suggest:   { label: "Suggestive", description: "Messages suggested, rep reviews and sends" },
  disabled:  { label: "Manual",    description: "Rep handles all messaging" },
};

/**
 * Resolve effective automation mode for a source, respecting the global
 * messaging automation settings. When the global context enforces suggestive
 * mode or requireReviewBeforeSend, auto_send is downgraded to suggest.
 */
export function resolveEffectiveMode(
  profileMode: AutomationMode,
  globalShouldAutoSend: boolean,
): AutomationMode {
  if (profileMode === "disabled") return "disabled";
  if (!globalShouldAutoSend) return "suggest";
  return profileMode;
}
